import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { DividerText } from "src/components/base/divider";
import SearchDomainForm from "src/components/search-domain-form";
import React from "react";

/**
 * The properties for {@link SearchDomainTenantOverview}
 */
export type SearchDomainTenantOverviewProps = {};

/**
 * Search domain tenant overview
 */
export default function SearchDomainTenantOverview(props: SearchDomainTenantOverviewProps) {
    const [t] = useTranslation("tenant-dns");

    const router = useRouter();
    const { tenantId } = Route.useParams();
    const [{ domains, global_domains }, { dns_base_domain }] = Route.useLoaderData();

    return (
        <div className={"flex max-w-2xl flex-col gap-5"}>
            <div className={"flex flex-col gap-1"}>
                <Subheading>{t("heading.search-domains")}</Subheading>
                <Text>{t("description.search-domains")}</Text>
            </div>

            <div className={"flex max-w-2xl flex-col gap-2"}>
                <div
                    className={
                        "flex items-center justify-between gap-3 rounded-md bg-(--surface-muted)/40 p-3 ring-1 ring-zinc-950/5 dark:ring-white/10"
                    }
                >
                    <span className={"truncate text-sm/5 font-semibold text-zinc-600 dark:text-zinc-400"}>
                        {dns_base_domain}
                    </span>
                    <Badge title={t("description.base-domain")} className={"cursor-default"}>
                        <LockClosedIcon className={"size-3"} />
                        {t("label.base-domain")}
                    </Badge>
                </div>

                <DividerText soft={true} text={t("divider.reorderable")} className={"my-1"} />

                <SearchDomainForm
                    domains={domains}
                    tenant={tenantId}
                    onChange={async () => {
                        await router.invalidate({ sync: true });
                    }}
                />

                <DividerText soft={true} text={t("divider.global-serach-domains")} className={"my-1"} />

                {global_domains.map((domain) => (
                    <div
                        key={domain.uuid}
                        className={
                            "flex items-center justify-between gap-3 rounded-md bg-(--surface-muted)/40 p-3 ring-1 ring-zinc-950/5 dark:ring-white/10"
                        }
                    >
                        <span className={"truncate text-sm/5 font-semibold text-zinc-600 dark:text-zinc-400"}>
                            {domain.domain}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant/search-domain")({
    component: SearchDomainTenantOverview,
    loader: async ({ params }) =>
        await Promise.all([ApiInternal.tenantsDns.getSearchDomain(params.tenantId), ApiInternal.settings.getGeneral()]),
});
